import { Page } from '$core/models/page';
import { PagesService } from '$core/services/pages.service';
import { environment } from '$env/environment';
import { Injectable, OnDestroy, Optional } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { LocalizeRouterService } from 'localize-router';
import { debounceTime, filter } from 'rxjs/operators';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root',
})
export class SeoService implements OnDestroy {
  private currentPage: Page | null;
  private defaultTitle = this.translateService.instant('BELPOST');
  private titlePrefix = '';
  private canonicalLink: HTMLLinkElement;
  private alternateLinks: HTMLLinkElement[] = [];
  private tags: HTMLMetaElement[] = [];

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private localizeRouterService: LocalizeRouterService,
    private pagesService: PagesService,
    @Optional() private gtmService: GoogleTagManagerService
  ) {
    if (!environment.production) {
      this.titlePrefix = `[${environment.name}] `;
    }

    this.titleService.setTitle(this.titlePrefix + this.defaultTitle);
    this.pagesService.page$
      .pipe(filter(Boolean), debounceTime(100))
      .subscribe((page: Page) => {
        this.currentPage = page;
        this.clean();
        this.set();
        if (environment.production && !window['webpackHotUpdate']) {
          this.pushPageToGtag();
        }
      });
  }

  private pushPageToGtag() {
    if (!window['gtag']) {
      return;
    }
    if (this.gtmService) {
      this.gtmService.pushTag({
        event: 'page',
        pageName: this.titleService.getTitle(),
      });
    }
  }

  private set() {
    if (this.currentPage) {
      this.titleService.setTitle(this.titlePrefix + this.currentPage.title);
      this.addTags();
    } else {
      this.titleService.setTitle(this.titlePrefix + this.defaultTitle);
    }
    this.addCanonicalURL();
    this.addAlternateLinks();
  }

  private clean() {
    this.removeRenderStatusCode();
    this.removeCanonicalLink();
    this.removeAlternateLinks();
    this.removeTags();
  }

  public addCanonicalURL() {
    this.canonicalLink = document.createElement('link');
    this.canonicalLink.setAttribute('rel', 'canonical');
    this.canonicalLink.setAttribute('href', document.URL.split(/[?#]/)[0]);
    document.head.appendChild(this.canonicalLink);
  }

  private removeCanonicalLink() {
    if (this.canonicalLink) {
      this.canonicalLink.remove();
    }
  }

  public addAlternateLinks() {
    const locales = this.localizeRouterService.parser.locales;
    const defaultLang = this.localizeRouterService.parser.defaultLang;

    locales.forEach((locale) => {
      const link: HTMLLinkElement = document.createElement('link');

      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', locale);

      let href = this.languageService.traslateCurrentURL(locale);
      if (locale === defaultLang) {
        href = href.replace(`/${locale}`, '');
      }
      href = href.split(/[?#]/)[0]; // Remove querystring and fragments from URL
      link.setAttribute('href', href);
      document.head.appendChild(link);
      this.alternateLinks.push(link);
    });
  }

  private removeAlternateLinks() {
    if (this.alternateLinks.length) {
      this.alternateLinks.forEach((el) => el.remove());
      this.alternateLinks = [];
    }
  }

  public addTags() {
    const page = this.currentPage;
    const tags: MetaDefinition[] = [
      {
        property: 'og:title',
        content: page.getMetaTag('title') || page.title,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: document.URL.split(/[?#]/)[0],
      },
      {
        property: 'og:locale',
        content: this.languageService.currentLang,
      },
    ];

    const description = page.getMetaTag('description') || page.short_content;
    if (description) {
      tags.push(
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:description',
          content: description,
        }
      );
    }

    if (page.thumbnail_image) {
      tags.push(
        {
          property: 'og:image',
          content: page.thumbnail_image.path,
        },
        {
          property: 'og:image:alt',
          content: page.thumbnail_image.display_name || '',
        },
        {
          property: 'og:image:url',
          content: page.thumbnail_image.path,
        }
      );

      if (page.thumbnail_image.path.startsWith('https://')) {
        tags.push({
          property: 'og:image:secure_url',
          content: page.thumbnail_image.path,
        });
      }
    }

    if (page.getMetaTag('keywords')) {
      tags.push({
        name: 'keywords',
        content: page.getMetaTag('keywords'),
      });
    }

    if (page.status_code) {
      this.addRenderStatusCode(page.status_code);
    }

    this.tags = this.metaService.addTags(tags);
  }

  private removeTags() {
    if (this.tags.length) {
      this.tags.forEach((tag) => this.metaService.removeTagElement(tag));
      this.tags = [];
    }
  }

  public addRenderStatusCode(code: number | string) {
    this.metaService.addTag({
      name: 'render:status_code',
      content: code.toString(),
    });
  }

  private removeRenderStatusCode() {
    this.metaService.removeTag("name='render:status_code'");
  }

  public ngOnDestroy() {
    this.clean();
  }
}
